import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom"; // Import useLocation
import { useAuth } from "../context/AuthContext";
import { FaSave, FaArrowLeft } from "react-icons/fa"; // Icons for Save and Back

function EditUserForm() {
  const { id } = useParams(); // Get the user ID from the URL
  const navigate = useNavigate();
  const location = useLocation(); // Get location object to access state
  const { logout } = useAuth();
  const storedToken = localStorage.getItem("token");

  // State to store previous page details passed from AllUsers
  const [prevPageState, setPrevPageState] = useState({
    prevDay: 'day1', // Default to day1
    prevPage: 1      // Default to page 1
  });

  const [formData, setFormData] = useState({
    firm_name: "",
    arc_name: "",
    designation: "",
    arc_image: null, // This will store the URL of the existing image
    arc_quote: "",
    contact_number: "",
    firm_website: "",
    personal_insta: "",
    firm_insta: "",
    personal_linkedin: "",
    firm_linkedin: "",
    projects_2025: "", // Will be stringified array
    pick_up_location: "",
    // drop_location is not editable, so not included here
    pick_up_time: "", // Will be a string for input type="datetime-local"
    evening_drop_location: "",
    event_day: "",
  });

  const [arcImageFile, setArcImageFile] = useState(null); // To store the new file object
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null); // Used for form-level errors (e.g., failed to fetch data)

  // State for toast notifications
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success"); // 'success' or 'danger'

  // Helper function to format UTC string to IST for display (e.g., below the input field)
  const formatToIST = (utcString) => {
    if (!utcString) return "N/A";
    const date = new Date(utcString);
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }
    return date.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  // Workaround Helper function to adjust UTC date to display as specific IST time in datetime-local input.
  // This function assumes the backend is sending UTC time that is consistently
  // 5 hours 30 minutes *earlier* than the actual UTC time that would correctly
  // represent the desired IST display time (e.g., 10:00 AM IST).
  const adjustUtcToDisplayIST = (utcString) => {
    if (!utcString) return "";
    const date = new Date(utcString); // This date object is initially in UTC based on the string
    if (isNaN(date.getTime())) return "";

    // Add 5 hours and 30 minutes to the UTC time received from the backend.
    // This compensates for the backend sending a UTC time (e.g., 23:00Z from previous day)
    // that, when converted to IST, is 04:30 IST, but the user expects 10:00 IST.
    // So, we shift it forward by 5 hours 30 minutes to get to the 10:00 mark.
    date.setUTCHours(date.getUTCHours() + 5);
    date.setUTCMinutes(date.getUTCMinutes() + 30);

    // Now, format this *adjusted* UTC date into the YYYY-MM-DDTHH:mm format
    // The datetime-local input will then display this adjusted UTC time as if it's local.
    const year = date.getUTCFullYear();
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = date.getUTCDate().toString().padStart(2, '0');
    const hour = date.getUTCHours().toString().padStart(2, '0');
    const minute = date.getUTCMinutes().toString().padStart(2, '0');

    return `${year}-${month}-${day}T${hour}:${minute}`;
  };


  useEffect(() => {
    // Capture previous page state from location.state when component mounts
    if (location.state && location.state.prevDay && location.state.prevPage) {
      setPrevPageState({
        prevDay: location.state.prevDay,
        prevPage: location.state.prevPage
      });
    }

    const fetchUserData = async () => {
      setLoading(true);
      setError(null); // Clear any previous errors
      try {
        if (!id) {
            throw new Error("No user ID provided for editing.");
        }

        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/dataforms/${id}`, // Assuming a GET endpoint for single user
          {
            headers: { Authorization: `Bearer ${storedToken}` },
          }
        );

        if (res.status === 401) {
          logout();
          navigate("/"); // Redirect to login if unauthorized
          return;
        }

        // Check if the response is JSON before parsing
        const contentType = res.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
            const textError = await res.text(); // Read as text to see the HTML/plain error
            console.error("Server responded with non-JSON content:", textError);
            throw new Error(`Server did not return JSON. Content-Type: ${contentType}. Response: ${textError.substring(0, 100)}...`);
        }

        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.message || "Failed to fetch user data.");
        }

        // Log the raw pick_up_time from the backend for debugging
        console.log("Raw pick_up_time from backend:", data.pick_up_time);

        // Pre-fill form data with fetched values
        setFormData({
          firm_name: data.firm_name || "",
          arc_name: data.arc_name || "",
          designation: data.designation || "",
          arc_image: data.arc_image || null, // Set existing image URL
          arc_quote: data.arc_quote || "",
          contact_number: data.contact_number || "",
          firm_website: data.firm_website || "",
          personal_insta: data.personal_insta || "",
          firm_insta: data.firm_insta || "",
          personal_linkedin: data.personal_linkedin || "",
          firm_linkedin: data.firm_linkedin || "",
          // Convert projects_2025 from JSON string to comma-separated string for textarea
          projects_2025: data.projects_2025
            ? (typeof data.projects_2025 === 'string'
                ? JSON.parse(data.projects_2025)
                : data.projects_2025 // If not a string, assume it's already an array or null
              ).join(", ")
            : "",
          pick_up_location: data.pick_up_location || "",
          // Format pick_up_time for datetime-local input using the workaround function
          pick_up_time: data.pick_up_time
            ? adjustUtcToDisplayIST(data.pick_up_time)
            : "",
          evening_drop_location: data.evening_drop_location || "",
          event_day: data.event_day || "",
        });
      } catch (err) {
        console.error("Error fetching user data:", err);
        setError(err.message || "Error loading user data.");
      } finally {
        setLoading(false);
      }
    };

    // Fetch data only if a token exists (user is likely authenticated)
    if (storedToken) {
      fetchUserData();
    } else {
        setLoading(false);
        setError("Authentication token missing. Please log in.");
        // Optionally, redirect to login immediately if no token
        // navigate("/");
    }
  }, [id, navigate, logout, storedToken, location.state]); // Dependencies for useEffect

  // useEffect for toast visibility management
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
        setToastMessage("");
        setToastType("success"); // Reset to default type
      }, 3000); // Toast disappears after 3 seconds
      return () => clearTimeout(timer);
    }
  }, [showToast]);


  // Handles changes to form input fields
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  // Handles changes to the image file input
  const handleFileChange = (e) => {
    setArcImageFile(e.target.files[0]);
    // Update the image preview immediately if a file is selected
    if (e.target.files[0]) {
      setFormData((prevData) => ({
        ...prevData,
        arc_image: URL.createObjectURL(e.target.files[0]),
      }));
    } else {
      // If no file selected, clear the preview (or ideally revert to original image URL)
      setFormData((prevData) => ({
        ...prevData,
        arc_image: null,
      }));
    }
  };

  // Handles form submission (PATCH request to update user data)
  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission behavior
    setSubmitting(true); // Indicate that submission is in progress
    setError(null); // Clear any previous form errors
    setShowToast(false); // Hide any active toast before new submission

    const dataToSend = new FormData(); // FormData for handling file uploads

    // Append all text fields from formData to dataToSend
    for (const key in formData) {
      if (key === "arc_image") continue; // Skip image URL, handle file separately
      if (key === "projects_2025") {
        // Convert comma-separated string back to JSON array string for backend
        const projectsArray = formData.projects_2025
          .split(",")
          .map((item) => item.trim())
          .filter((item) => item !== "");
        dataToSend.append(key, JSON.stringify(projectsArray));
      } else {
        dataToSend.append(key, formData[key]);
      }
    }

    // Append the new image file if selected
    if (arcImageFile) {
      dataToSend.append("arc_image", arcImageFile); // 'arc_image' must match your Multer fieldname on backend
    }

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/dataforms/${id}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${storedToken}`,
            // IMPORTANT: Do NOT set 'Content-Type': 'multipart/form-data' here.
            // The browser sets it automatically with the correct boundary when you use FormData.
          },
          body: dataToSend,
        }
      );

      if (res.status === 401) {
        logout();
        navigate("/"); // Redirect to login if unauthorized
        return;
      }

      const resData = await res.json(); // Parse response as JSON

      if (!res.ok) {
        // If response is not OK (e.g., 400, 500), throw an error
        throw new Error(resData.message || "Failed to update user data.");
      }

      // Show success toast
      setToastMessage("User data updated successfully!");
      setToastType("success");
      setShowToast(true);

      // Navigate back to the specific page and day in AllUsers after a short delay
      setTimeout(() => {
        navigate("/users", {
          state: {
            message: "User data updated successfully!", // Message for AllUsers toast
            prevDay: prevPageState.prevDay, // Pass previous day
            prevPage: prevPageState.prevPage // Pass previous page
          }
        });
      }, 2000); // Navigate after 2 seconds
    } catch (err) {
      console.error("Error updating user data:", err);
      // Show error toast
      setToastMessage(err.message || "Error updating user data. Please try again.");
      setToastType("danger");
      setShowToast(true);
    } finally {
      setSubmitting(false); // Reset submitting state
    }
  };

  // Render loading state
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="ms-2">Loading user data...</p>
      </div>
    );
  }

  // Render persistent error message if no toast is showing
  if (error && !showToast) {
    return (
      <div className="container mt-5">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
        <button className="btn btn-secondary" onClick={() => navigate("/users")}>
          <FaArrowLeft className="me-2" /> Back to Users
        </button>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <h2 className="text-center mb-4">Edit User Data</h2>

      {/* Toast Notification positioned fixed top-right */}
      {showToast && (
        <div
          className={`alert alert-${toastType} alert-dismissible fade show fixed-top-right`}
          role="alert"
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 1050, // Ensures toast is above other elements like modals
            minWidth: '250px'
          }}
        >
          {toastMessage}
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="alert"
            aria-label="Close"
            onClick={() => setShowToast(false)}
          ></button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="needs-validation" noValidate>
        <div className="row g-3">
          {/* Firm Name */}
          <div className="col-md-6">
            <label htmlFor="firm_name" className="form-label">
              Firm Name
            </label>
            <input
              type="text"
              className="form-control"
              id="firm_name"
              name="firm_name"
              value={formData.firm_name}
              onChange={handleChange}
              required // Added required attribute back for validation
            />
            <div className="invalid-feedback">Firm name is required.</div>
          </div>

          {/* Architect Name */}
          <div className="col-md-6">
            <label htmlFor="arc_name" className="form-label">
              Architect Name
            </label>
            <input
              type="text"
              className="form-control"
              id="arc_name"
              name="arc_name"
              value={formData.arc_name}
              onChange={handleChange}
              required // Added required attribute back for validation
            />
            <div className="invalid-feedback">Architect name is required.</div>
          </div>

          {/* Designation */}
          <div className="col-md-6">
            <label htmlFor="designation" className="form-label">
              Designation
            </label>
            <input
              type="text"
              className="form-control"
              id="designation"
              name="designation"
              value={formData.designation}
              onChange={handleChange}
            />
          </div>

          {/* Architect Image */}
          <div className="col-md-6">
            <label htmlFor="arc_image_upload" className="form-label">
              Architect Image
            </label>
            <input
              type="file"
              className="form-control"
              id="arc_image_upload"
              name="arc_image_upload"
              accept="image/*"
              onChange={handleFileChange}
            />
            {formData.arc_image && (
              <div className="mt-2">
                <img
                  src={formData.arc_image}
                  alt="Current Architect"
                  className="img-thumbnail"
                  style={{ width: "100px", height: "100px", objectFit: "cover" }}
                />
              </div>
            )}
          </div>

          {/* Architect Quote */}
          <div className="col-md-12">
            <label htmlFor="arc_quote" className="form-label">
              Architect Quote
            </label>
            <textarea
              className="form-control"
              id="arc_quote"
              name="arc_quote"
              rows="3"
              value={formData.arc_quote}
              onChange={handleChange}
            ></textarea>
          </div>

          {/* Contact Number */}
          <div className="col-md-6">
            <label htmlFor="contact_number" className="form-label">
              Contact Number
            </label>
            <input
              type="text"
              className="form-control"
              id="contact_number"
              name="contact_number"
              value={formData.contact_number}
              onChange={handleChange}
            />
          </div>

          {/* Firm Website */}
          <div className="col-md-6">
            <label htmlFor="firm_website" className="form-label">
              Firm Website
            </label>
            <input
              type="url"
              className="form-control"
              id="firm_website"
              name="firm_website"
              value={formData.firm_website}
              onChange={handleChange}
            />
          </div>

          {/* Personal Instagram */}
          <div className="col-md-6">
            <label htmlFor="personal_insta" className="form-label">
              Personal Instagram
            </label>
            <input
              type="url"
              className="form-control"
              id="personal_insta"
              name="personal_insta"
              value={formData.personal_insta}
              onChange={handleChange}
            />
          </div>

          {/* Firm Instagram */}
          <div className="col-md-6">
            <label htmlFor="firm_insta" className="form-label">
              Firm Instagram
            </label>
            <input
              type="url"
              className="form-control"
              id="firm_insta"
              name="firm_insta"
              value={formData.firm_insta}
              onChange={handleChange}
            />
          </div>

          {/* Personal LinkedIn */}
          <div className="col-md-6">
            <label htmlFor="personal_linkedin" className="form-label">
              Personal LinkedIn
            </label>
            <input
              type="url"
              className="form-control"
              id="personal_linkedin"
              name="personal_linkedin"
              value={formData.personal_linkedin}
              onChange={handleChange}
            />
          </div>

          {/* Firm LinkedIn */}
          <div className="col-md-6">
            <label htmlFor="firm_linkedin" className="form-label">
              Firm LinkedIn
            </label>
            <input
              type="url"
              className="form-control"
              id="firm_linkedin"
              name="firm_linkedin"
              value={formData.firm_linkedin}
              onChange={handleChange}
            />
          </div>

          {/* Projects 2025 */}
          <div className="col-md-12">
            <label htmlFor="projects_2025" className="form-label">
              Projects 2025 (comma-separated)
            </label>
            <textarea
              className="form-control"
              id="projects_2025"
              name="projects_2025"
              rows="3"
              value={formData.projects_2025}
              onChange={handleChange}
            ></textarea>
            <small className="form-text text-muted">
              Enter projects separated by commas (e.g., Project A, Project B)
            </small>
          </div>

          {/* Pick Up Location */}
          <div className="col-md-6">
            <label htmlFor="pick_up_location" className="form-label">
              Pick Up Location
            </label>
            <input
              type="text"
              className="form-control"
              id="pick_up_location"
              name="pick_up_location"
              value={formData.pick_up_location}
              onChange={handleChange}
            />
          </div>

          {/* Pick Up Time */}
          <div className="col-md-6">
            <label htmlFor="pick_up_time" className="form-label">
              Pick Up Time
            </label>
            <input
              type="datetime-local"
              className="form-control"
              id="pick_up_time"
              name="pick_up_time"
              value={formData.pick_up_time} // This value is now correctly formatted for IST
              onChange={handleChange}
            />
            {/* Display IST time below the input */}
            {formData.pick_up_time && (
              <small className="form-text text-muted mt-1">
                Current IST: {formatToIST(formData.pick_up_time)}
              </small>
            )}
          </div>

          {/* Evening Drop Location */}
          <div className="col-md-6">
            <label htmlFor="evening_drop_location" className="form-label">
              Evening Drop Location
            </label>
            <input
              type="text"
              className="form-control"
              id="evening_drop_location"
              name="evening_drop_location"
              value={formData.evening_drop_location}
              onChange={handleChange}
            />
          </div>

          {/* Event Day */}
          <div className="col-md-6">
            <label htmlFor="event_day" className="form-label">
              Event Day
            </label>
            <select
              className="form-select"
              id="event_day"
              name="event_day"
              value={formData.event_day}
              onChange={handleChange}
              required // Added required attribute back for validation
            >
              <option value="">Select Day</option>
              <option value="day1">Day 1</option>
              <option value="day2">Day 2</option>
            </select>
            <div className="invalid-feedback">Event day is required.</div>
          </div>

          {/* Drop Location (Non-editable) */}
          <div className="col-md-12">
            <label htmlFor="drop_location" className="form-label">
              Drop Location (Non-editable)
            </label>
            <input
              type="text"
              className="form-control"
              id="drop_location"
              name="drop_location"
              value="Leela Bhartiya City, Bangalore" // This value is fixed as per your model
              readOnly
              disabled // Visually indicate it's disabled
            />
            <small className="form-text text-muted">
              This field is not editable.
            </small>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="d-flex justify-content-end gap-2 mt-4">
          <button
            type="button"
            className="btn btn-secondary"
            // Navigate back to the specific page and day in AllUsers
            onClick={() => navigate("/users", { state: { prevDay: prevPageState.prevDay, prevPage: prevPageState.prevPage } })}
            disabled={submitting}
          >
            <FaArrowLeft className="me-2" /> Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitting}
          >
            {submitting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Updating...
              </>
            ) : (
              <>
                <FaSave className="me-2" /> Update User
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default EditUserForm;
